import { ProxySession } from '@armhr/ui';
import { AxiosInstance, AxiosResponse } from 'axios';

export const getProxySession = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<ProxySession | null>> => {
  const response: AxiosResponse<ApiResponse<ProxySession | null>> =
    await axiosInstance.get('/admin/proxy-session');
  return response.data;
};

export const getUserLoginEmail = async (
  axiosInstance: AxiosInstance,
  id: string
): Promise<ApiResponse<any>> => {
  const response: AxiosResponse<ApiResponse<any>> = await axiosInstance.get(
    `/admin/users/${id}/email`
  );

  return response.data;
};

export const getUserDetails = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<UserDetails[]>> => {
  const response: AxiosResponse<ApiResponse<UserDetails[]>> =
    await axiosInstance.get(`/admin/user-permissions`);
  return response.data;
};

export const getAllowedEmployees = async (
  axiosInstance: AxiosInstance,
  employeeId: string
): Promise<ApiResponse<AllowedEmployee[]>> => {
  const response: AxiosResponse<ApiResponse<AllowedEmployee[]>> =
    await axiosInstance.get(
      `/admin/user-permissions/${employeeId}/allowed-employees`
    );
  return response.data;
};

export const getBackOfficeRedirect = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<string>> => {
  const response: AxiosResponse<ApiResponse<string>> = await axiosInstance.get(
    '/admin/back-office'
  );
  return response.data;
};

export const getEmployeeBonusTaxWithholdings = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<BonusTaxWithholdingRequest[]>> => {
  const response: AxiosResponse<ApiResponse<BonusTaxWithholdingRequest[]>> =
    await axiosInstance.get('/admin/bonus-tax-withholding');
  return response.data;
};

export const assignBonusTaxWithholdings = async (
  axiosInstance: AxiosInstance,
  body: BonusTaxWithholdingAssignRequest
): Promise<ApiResponse<BonusTaxWithholdingRequest[]>> => {
  const response: AxiosResponse<ApiResponse<BonusTaxWithholdingRequest[]>> =
    await axiosInstance.post('/admin/bonus-tax-withholding', body);
  return response.data;
};

export const deleteBonusTaxWithholding = async (
  axiosInstance: AxiosInstance,
  request_id: number
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.delete(`/admin/bonus-tax-withholding/${request_id}`);
  return response.data;
};

export const getPasswordResetUsers = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<Auth0UserForPasswordReset[]>> => {
  const response: AxiosResponse<ApiResponse<Auth0UserForPasswordReset[]>> =
    await axiosInstance.get('/admin/password-reset-users');
  return response.data;
};

export const sendPasswordReset = async (
  axiosInstance: AxiosInstance,
  payload: { email: string }
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.post('/admin/send-password-reset', payload);
  return response.data;
};

export const getCustomFieldDefinitions = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CustomFieldDefinition[]>> => {
  const response: AxiosResponse<ApiResponse<CustomFieldDefinition[]>> =
    await axiosInstance.get(`/admin/custom-fields`);
  return response.data;
};

export const createCustomFieldDefinition = async (
  axiosInstance: AxiosInstance,
  clientId: string,
  fieldData: CustomFieldDefinitionCreate
): Promise<ApiResponse<CustomFieldDefinition>> => {
  const response: AxiosResponse<ApiResponse<CustomFieldDefinition>> =
    await axiosInstance.post(`/admin/custom-fields`, fieldData);
  return response.data;
};

export const updateCustomFieldDefinition = async (
  axiosInstance: AxiosInstance,
  clientId: string,
  fieldId: number,
  fieldData: CustomFieldDefinitionUpdate
): Promise<ApiResponse<CustomFieldDefinition>> => {
  const response: AxiosResponse<ApiResponse<CustomFieldDefinition>> =
    await axiosInstance.put(`/admin/custom-fields/${fieldId}`, fieldData);
  return response.data;
};

export const deleteCustomFieldDefinition = async (
  axiosInstance: AxiosInstance,
  clientId: string,
  fieldId: number
): Promise<ApiResponse<{ status: string }>> => {
  const response: AxiosResponse<ApiResponse<{ status: string }>> =
    await axiosInstance.delete(`/admin/custom-fields/${fieldId}`);
  return response.data;
};

export const getCustomFieldsReport = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<CustomFieldsReportData>> => {
  const response: AxiosResponse<ApiResponse<CustomFieldsReportData>> =
    await axiosInstance.get(`/admin/custom-fields/report`);
  return response.data;
};

export const downloadCustomFieldsReportCsv = async (
  axiosInstance: AxiosInstance
): Promise<Blob> => {
  const response = await axiosInstance.get(`/admin/custom-fields/report/csv`, {
    responseType: 'blob',
  });
  return response.data;
};

export const getActiveAlerts = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<AppAlert[]>> => {
  const response: AxiosResponse<ApiResponse<AppAlert[]>> =
    await axiosInstance.get('/admin/alerts/active');
  return response.data;
};
export const getHandbookManagerConfig = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<ManagerHandbookConfig[]>> => {
  const response: AxiosResponse<ApiResponse<ManagerHandbookConfig[]>> =
    await axiosInstance.get('/admin/handbooks/config');
  return response.data;
};

export const setHandbookManagerConfig = async (
  axiosInstance: AxiosInstance,
  payload: SetHandbookConfigPayload
): Promise<ApiResponse<boolean>> => {
  const response: AxiosResponse<ApiResponse<boolean>> =
    await axiosInstance.post('/admin/handbooks/config', payload);
  return response.data;
};

export const adminService = {
  getAllowedEmployees,
  getActiveAlerts,
  getProxySession,
  getUserLoginEmail,
  getUserDetails,
  getBackOfficeRedirect,
  getEmployeeBonusTaxWithholdings,
  assignBonusTaxWithholdings,
  deleteBonusTaxWithholding,
  getPasswordResetUsers,
  sendPasswordReset,
  getCustomFieldDefinitions,
  createCustomFieldDefinition,
  updateCustomFieldDefinition,
  deleteCustomFieldDefinition,
  getCustomFieldsReport,
  downloadCustomFieldsReportCsv,
  getHandbookManagerConfig,
  setHandbookManagerConfig,
};
